import {
  BankTransactionModel,
  BankTransactionMutationInput,
} from 'api-hooks/transactions/bank-transaction';
import { TransactionalStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import { HalfContainer } from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import { SectionContainer, Title, TitleContainer } from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import { BankTransactionFormType, bankTransactionSchema } from './form-type';
import TransactionSection from './transaction-section';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  bankTransaction?: BankTransactionModel;
  onSubmit: (
    input: BankTransactionMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<BankTransactionModel | undefined>;
}

export function BankTransactionForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { bankTransaction } = props;

  const defaultValues = useGetDefaultValues({ bankTransaction });

  const resolver = useYupValidationResolver(bankTransactionSchema());

  const methods = useForm<BankTransactionFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('bank-transactions');
  const { onVoid, voidMutate, voidPermission } = useVoidAction(
    'bank-transactions',
    bankTransaction,
  );

  const { resolverButton } = useResolveApproval(
    'bank-transactions',
    bankTransaction,
  );

  const { requestApprovalButton } = useApprovalAction(
    'bank-transactions',
    bankTransaction,
  );

  const isActive = bankTransaction?.status === TransactionalStatusEnum.Active;

  const { revertDraftButton } = useRevertDraftAction(
    'bank-transactions',
    bankTransaction,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (bankTransaction) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    bankTransaction,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!bankTransaction}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        bankTransaction &&
        `${t('common:bank_transaction')} - ${bankTransaction.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={bankTransaction}
            onDelete={onVoid}
            noDelete={editable || !voidPermission}
            noEdit={!isActive || !permission.update}
            title={
              bankTransaction ? (
                <TitleContainer>
                  <Title variant="HeadingXSmall">{`${t(
                    'common:bank_transaction',
                  )} - ${bankTransaction.number}`}</Title>
                  <TransactionPillStatus value={bankTransaction.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', {
                  extra: t('common:bank_transaction'),
                })
              )
            }
            isDeleteLoading={voidMutate.isLoading}
            deleteText={t('common:void')}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          {bankTransaction && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:code')}
                placeholder={t('common:code')}
                disabled
                required
              />
            </HalfContainer>
          )}
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
              hasTime
              required
            />
          </HalfContainer>
        </SectionContainer>
        <TransactionSection />
      </div>
    </Form>
  );
}
